import { Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, FormControl, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NgxMatIntlTelInputComponent } from 'ngx-mat-intl-tel-input';
import parsePhoneNumberFromString from 'libphonenumber-js';
import { ThemeService } from 'src/theme';
import { AngularMaterialModule } from 'src/app/material.module';
import { BaseComponent } from '@lib/components';
import { ApiResponseError, IAccount, ICustomer, UserType } from '@lib/interfaces';
import { ISenderInfo } from '@lib/interfaces/super-agent-transfer.interface';
import { CustomersService } from '@lib/services/customer/customer.service';
import { SidePanelService } from '@lib/services';
import { EnterAmountComponent } from '../enter-amount/enter-amount.component';

@Component({
    selector: 'app-select-recipient',
    standalone: true,
    templateUrl: './select-recipient.component.html',
    styleUrls: ['./select-recipient.component.scss'],
    imports: [AngularMaterialModule, ReactiveFormsModule, NgxMatIntlTelInputComponent],
})
export class SelectRecipientComponent extends BaseComponent implements OnInit {
    protected mobileControl = new FormControl('', [Validators.required, this.validatePhoneNumber()]);
    protected loading = false;
    protected allowedCountriesInPhoneSearch: string[] = [];

    constructor(
        private readonly dialogRef: MatDialogRef<SelectRecipientComponent>,
        private readonly sidePanel: SidePanelService,
        private readonly customersService: CustomersService,
        private readonly snackbar: MatSnackBar,
        private readonly themeService: ThemeService,
        @Inject(MAT_DIALOG_DATA) public readonly data: { senderAccounts: IAccount[]; senderInfo: ISenderInfo },
    ) {
        super();
    }

    ngOnInit(): void {
        this.allowedCountriesInPhoneSearch = this.themeService.getActiveThemeAssets('allowedCountriesInPhoneSearch');
    }

    protected continue(): void {
        this.mobileControl.markAsTouched();
        this.mobileControl.markAsDirty();

        if (this.mobileControl.invalid) return;

        const ownMobile = JSON.parse(localStorage.getItem('userInfo') || '{}').mobile;
        if (ownMobile === this.mobileControl.value) {
            this.createApiErrorSnackBar(this.snackbar, 'You cannot top up your own wallet using this flow.');
            return;
        }

        this.loading = true;
        this.addSubscription(
            this.customersService
                // Intentionally not filtering by is_super_agent here — the ticket's AC9 says
                // `true`, which contradicts its own Background section ("Super Agents top up
                // Regular Agents"). Left to the authoritative transferToAgent eligibility check
                // instead (AC13: "Backend enforces eligibility even if UI fails").
                .filterCustomers({
                    mobile: this.mobileControl.value || '',
                    user_type: UserType.AGENT,
                })
                .subscribe({
                    next: (response) => {
                        this.loading = false;

                        if (response.data.length === 0) {
                            this.createApiErrorSnackBar(this.snackbar, 'Please, this flow for agents only. You can top-up for customers using normal Top-Up flow.');
                            return;
                        }

                        // AC11 — multiple matches shouldn't happen; take the first and let the
                        // authoritative transferToAgent call re-validate eligibility server-side.
                        const recipientInfo: ICustomer = response.data[0];

                        this.dialogRef.close();
                        const dialogRef = this.sidePanel.open(EnterAmountComponent);
                        dialogRef.componentInstance.recipientInfo = recipientInfo;
                        dialogRef.componentInstance.senderAccounts = this.data.senderAccounts;
                        dialogRef.componentInstance.senderInfo = this.data.senderInfo;
                    },
                    error: (error: ApiResponseError) => {
                        this.loading = false;
                        this.createApiErrorSnackBar(this.snackbar, error);
                    },
                }),
        );
    }

    protected cancel(): void {
        this.dialogRef.close();
    }

    private validatePhoneNumber(): (control: AbstractControl) => ValidationErrors | null {
        return (control: AbstractControl): ValidationErrors | null => {
            const phoneNumber = control.value;

            if (!phoneNumber) return null;
            if (!this.allowedCountriesInPhoneSearch?.length) return null;

            try {
                const allowed = this.allowedCountriesInPhoneSearch.map((c) => c.toUpperCase());
                return allowed.includes(parsePhoneNumberFromString(phoneNumber as string)?.country || '') ? null : { validatePhoneNumber: true };
            } catch {
                return { validatePhoneNumber: true };
            }
        };
    }
}
